import React, { useState, useEffect } from "react";
import {
  Tabs,
  Tab,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import {
  Plus,
  SearchIcon,
  Settings,
  RotateCw,
  FilterX,
  X,
  Pencil,
  ChevronDown,
  ChevronUp,
  Calendar,
  Package,
  User,
  Clock,
  FileText,
  Hash,
  Scissors,
  Bell,
  Timer,
  Eye,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

// Define default columns for settings
const defaultColumns = [
  { id: "jobId", label: "Job ID", required: true },
  { id: "date", label: "Date", required: true },
  { id: "karigar", label: "Karigar", required: true },
  { id: "jobType", label: "Job Type", required: true },
  { id: "manualChallanNo", label: "Manual Challan No", required: false },
  { id: "designName", label: "Design Name/Number", required: true },
  { id: "expectedDate", label: "Expected Date", required: false },
];

const sortOptions = [
  { value: "newest", label: "Newest First" },
  { value: "oldest", label: "Oldest First" },
  { value: "productAZ", label: "Product (A-Z)" },
  { value: "productZA", label: "Product (Z-A)" },
];

// Sample data for demonstration
export const jobslips = [
  {
    id: "JS-1",
    date: "15 May, 2025",
    karigar: "John Doe",
    jobType: "Cutting",
    manualChallanNo: "MCN-001",
    designName: "Classic Shirt Design #123",
    expectedDate: "20 May, 2025",
    processingDays: 5,
    status: "pending",
    subJobs: [
      {
        id: "JS-1.1",
        date: "20 May, 2025",
        karigar: "Mike Wilson",
        jobType: "Stitching",
        expectedDate: "25 May, 2025",
        processingDays: 5,
        status: "pending",
      },
    ],
  },
  {
    id: "JS-2",
    date: "16 May, 2025",
    karigar: "Mike Wilson",
    jobType: "Stitching",
    manualChallanNo: "MCN-002",
    designName: "Summer Collection #A457",
    expectedDate: "22 May, 2025",
    processingDays: 7,
    status: "pending",
  },
  {
    id: "JS-3",
    date: "14 May, 2025",
    karigar: "Sarah Wilson",
    jobType: "Finishing",
    manualChallanNo: "MCN-003",
    designName: "Winter Jacket #WJ789",
    expectedDate: "18 May, 2025",
    processingDays: 4,
    status: "completed",
  },
];

const JobslipsComponent = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("pending");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState(
    defaultColumns.reduce((acc, col) => ({ ...acc, [col.id]: true }), {}),
  );
  const [filters, setFilters] = useState({
    dateRange: "",
    sortBy: "",
  });
  const [reminderModalOpen, setReminderModalOpen] = useState(false);
  const [selectedJobForReminder, setSelectedJobForReminder] = useState(null);
  const [reminderDateTime, setReminderDateTime] = useState(null);
  const [reminderDescription, setReminderDescription] = useState("");

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSearchChange = event => {
    setSearchQuery(event.target.value);
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  const handleSettingsClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseSettings = () => {
    setAnchorEl(null);
  };

  const handleColumnToggle = columnId => {
    const column = defaultColumns.find(col => col.id === columnId);
    if (column?.required) return;

    setVisibleColumns(prev => ({
      ...prev,
      [columnId]: !prev[columnId],
    }));
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    // Simulate refresh
    setTimeout(() => {
      setIsRefreshing(false);
    }, 1000);
  };

  const handleReminderClick = job => {
    setSelectedJobForReminder(job);
    setReminderModalOpen(true);
  };

  const handleSetReminder = async () => {
    try {
      // TODO: Implement reminder setting logic here
      console.log("Setting reminder for:", {
        jobId: selectedJobForReminder?.id,
        dateTime: reminderDateTime,
        description: reminderDescription,
      });

      // Close modal and reset state
      setReminderModalOpen(false);
      setSelectedJobForReminder(null);
      setReminderDateTime(null);
      setReminderDescription("");
    } catch (error) {
      console.error("Error setting reminder:", error);
    }
  };

  const clearAllFilters = () => {
    setSearchQuery("");
    setFilters({
      dateRange: "",
      sortBy: "",
    });
  };

  return (
    <div className="bg-white h-full">
      {/* Header Section */}
      <div className="flex items-center justify-between px-6 py-4 border-b">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-semibold font-outfit text-gray-800">
            Job Slips
          </h1>
          <button
            className="px-4 py-2 bg-[#5652B0] text-white rounded-lg text-sm font-medium 
                     hover:bg-[#4A47A3] transition-all duration-200 shadow-sm 
                     hover:shadow-md active:transform active:scale-95 
                     flex items-center gap-2"
            onClick={() => navigate("/production/create-jobslip")}>
            <Plus size={16} className="stroke-[2]" />
            Create Job Slip
          </button>
        </div>

        <div className="flex items-center gap-4">
          {/* Search Bar */}
          <div className="w-[450px]">
            <div className="relative">
              <SearchIcon
                className="absolute left-3 top-1/2 transform -translate-y-1/2"
                size={18}
                color="#83899F"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search by Job ID, Product, Karigar..."
                className="w-full h-10 pl-10 pr-10 rounded-lg border border-gray-200 
                         focus:border-[#5652B0] focus:ring-1 focus:ring-[#5652B0] 
                         outline-none transition-all text-gray-600 bg-white
                         hover:border-gray-300 focus:shadow-sm"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 
                           hover:bg-gray-100 p-1.5 rounded-full transition-all duration-200">
                  <X size={14} color="#83899F" />
                </button>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <IconButton
              onClick={handleRefresh}
              className="hover:bg-gray-100 transition-all duration-200"
              disabled={isRefreshing}
              title="Refresh">
              <RotateCw
                size={18}
                className={`text-gray-600 ${
                  isRefreshing ? "animate-spin" : ""
                }`}
              />
            </IconButton>
            <IconButton
              onClick={handleSettingsClick}
              className="hover:bg-gray-100 transition-all duration-200"
              title="Column Settings">
              <Settings size={18} className="text-gray-600" />
            </IconButton>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="px-6 py-4 flex items-center justify-between">
        {/* Tabs on the left */}
        <div>
          <div className="flex items-center gap-1 bg-gray-100/80 p-1 rounded-xl">
            <button
              onClick={() => setActiveTab("pending")}
              className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 
                       ${
                         activeTab === "pending"
                           ? "bg-white text-[#5652B0] shadow-sm ring-1 ring-black/5"
                           : "text-gray-600 hover:text-gray-900 hover:bg-white/50"
                       }`}>
              Pending Jobs
            </button>
            <button
              onClick={() => setActiveTab("completed")}
              className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 
                       ${
                         activeTab === "completed"
                           ? "bg-white text-[#5652B0] shadow-sm ring-1 ring-black/5"
                           : "text-gray-600 hover:text-gray-900 hover:bg-white/50"
                       }`}>
              Completed Jobs
            </button>
          </div>
        </div>

        {/* Filters on the right */}
        <div className="flex items-center gap-3">
          {/* Date Range Filter */}
          <FormControl
            size="small"
            sx={{
              minWidth: 180,
              "& .MuiOutlinedInput-root": {
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#E5E7EB",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#5652B0",
                  borderWidth: "1px",
                },
              },
            }}>
            <InputLabel>Date Range</InputLabel>
            <Select
              value={filters.dateRange}
              label="Date Range"
              onChange={e =>
                setFilters({ ...filters, dateRange: e.target.value })
              }>
              <MenuItem value="">All Time</MenuItem>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="yesterday">Yesterday</MenuItem>
              <MenuItem value="last7days">Last 7 Days</MenuItem>
              <MenuItem value="last30days">Last 30 Days</MenuItem>
              <MenuItem value="thisMonth">This Month</MenuItem>
            </Select>
          </FormControl>

          {/* Sort By Filter */}
          <FormControl
            size="small"
            sx={{
              minWidth: 180,
              "& .MuiOutlinedInput-root": {
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#E5E7EB",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#5652B0",
                  borderWidth: "1px",
                },
              },
            }}>
            <InputLabel>Sort By</InputLabel>
            <Select
              value={filters.sortBy}
              label="Sort By"
              onChange={e =>
                setFilters({ ...filters, sortBy: e.target.value })
              }>
              {sortOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Clear Filters Button */}
          {(searchQuery ||
            Object.values(filters).some(value => value !== "")) && (
            <button
              onClick={clearAllFilters}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700
                     bg-gray-50 hover:bg-gray-100 rounded-lg transition-all duration-200
                     border border-gray-200 hover:border-gray-300 active:bg-gray-200">
              <FilterX size={16} />
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Column Settings Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseSettings}
        PaperProps={{
          className: "mt-2",
          elevation: 0,
          sx: {
            border: "1px solid #E5E7EB",
            borderRadius: "0.5rem",
            minWidth: 180,
          },
        }}>
        <div className="px-3 py-2 border-b border-gray-100">
          <p className="text-sm font-medium text-gray-700">Show/Hide Columns</p>
        </div>
        {defaultColumns
          .filter(column => {
            // Only show expectedDate option in pending tab
            if (column.id === "expectedDate" && activeTab !== "pending") {
              return false;
            }
            return true;
          })
          .map(column => (
            <MenuItem
              key={column.id}
              onClick={() => handleColumnToggle(column.id)}
              disabled={column.required}
              sx={{ fontSize: "0.875rem" }}>
              <Checkbox
                checked={visibleColumns[column.id]}
                disabled={column.required}
                size="small"
                sx={{
                  color: "#5652B0",
                  "&.Mui-checked": {
                    color: "#5652B0",
                  },
                }}
              />
              {column.label}
            </MenuItem>
          ))}
      </Menu>

      {/* Reminder Modal */}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Dialog
          open={reminderModalOpen}
          onClose={() => {
            setReminderModalOpen(false);
            setSelectedJobForReminder(null);
            setReminderDateTime(null);
            setReminderDescription("");
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            style: {
              borderRadius: "12px",
            },
          }}>
          <DialogTitle className="flex justify-between items-center border-b pb-4">
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#5652B0]" />
              <span className="text-xl font-semibold text-gray-800">
                Set Reminder
              </span>
            </div>
            <button
              onClick={() => {
                setReminderModalOpen(false);
                setSelectedJobForReminder(null);
                setReminderDateTime(null);
                setReminderDescription("");
              }}
              className="text-gray-500 hover:text-gray-700 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </DialogTitle>
          <DialogContent>
            <div className="mt-6 flex flex-col gap-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-500 mb-1">Job Slip</div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-800">
                    {selectedJobForReminder?.id}
                  </span>
                  <span className="text-gray-400">•</span>
                  <span className="font-medium text-gray-800">
                    {selectedJobForReminder?.designName}
                  </span>
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  Karigar:{" "}
                  <span className="text-gray-800">
                    {selectedJobForReminder?.karigar}
                  </span>
                </div>
              </div>

              <DateTimePicker
                label="Reminder Date & Time"
                value={reminderDateTime}
                onChange={newValue => setReminderDateTime(newValue)}
                className="w-full"
                slotProps={{
                  textField: {
                    variant: "outlined",
                    fullWidth: true,
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        "&:hover fieldset": {
                          borderColor: "#5652B0",
                        },
                      },
                      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                        {
                          borderColor: "#5652B0",
                        },
                    },
                  },
                }}
              />

              <TextField
                label="Reminder Description"
                multiline
                rows={3}
                value={reminderDescription}
                onChange={e => setReminderDescription(e.target.value)}
                placeholder="Add a note about this reminder..."
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    "&:hover fieldset": {
                      borderColor: "#5652B0",
                    },
                  },
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor: "#5652B0",
                    },
                }}
              />
            </div>
          </DialogContent>
          <DialogActions className="border-t p-4 gap-3">
            <Button
              onClick={() => {
                setReminderModalOpen(false);
                setSelectedJobForReminder(null);
                setReminderDateTime(null);
                setReminderDescription("");
              }}
              variant="outlined"
              sx={{
                borderColor: "#5652B0",
                color: "#5652B0",
                "&:hover": {
                  borderColor: "#4A47A3",
                  backgroundColor: "rgba(86, 82, 176, 0.04)",
                },
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 500,
              }}>
              Cancel
            </Button>
            <Button
              onClick={handleSetReminder}
              variant="contained"
              disabled={!reminderDateTime}
              sx={{
                bgcolor: "#5652B0",
                "&:hover": {
                  bgcolor: "#4A47A3",
                },
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 500,
                boxShadow: "none",
                "&:hover": {
                  bgcolor: "#4A47A3",
                  boxShadow: "none",
                },
              }}>
              Set Reminder
            </Button>
          </DialogActions>
        </Dialog>
      </LocalizationProvider>

      {/* Updated Cards Layout */}
      <div className="px-6 pb-6">
        <div className="flex flex-col gap-3">
          {jobslips
            .filter(job => job.status === activeTab)
            .map(job => (
              <div key={job.id}>
                {/* Parent Job Card */}
                <div className="border border-gray-200 rounded-lg overflow-hidden bg-white hover:border-[#5652B0]/20 hover:shadow-sm transition-all duration-200">
                  <div className="p-4">
                    <div className="flex flex-col gap-3">
                      {/* First Line: Job ID + Design Name, Type, and Karigar */}
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 bg-[#5652B0]/5 px-3 py-1.5 rounded-md">
                          <span className="text-sm font-medium text-[#5652B0]">
                            {job.id}
                          </span>
                          <span className="text-gray-400">•</span>
                          <span className="text-sm font-medium text-gray-900">
                            {job.designName}
                          </span>
                        </div>
                        <span className="text-sm font-medium text-gray-600 bg-gray-100 px-3 py-1.5 rounded-md flex items-center gap-1.5">
                          <Scissors size={12} />
                          {job.jobType}
                        </span>
                        <div className="flex items-center gap-2">
                          <User size={16} className="text-[#5652B0]" />
                          <span className="text-sm font-medium text-[#5652B0]">
                            {job.karigar}
                          </span>
                        </div>

                        {/* Edit and Reminder Buttons */}
                        <div className="ml-auto flex items-center gap-2">
                          <button
                            className="p-2 rounded-lg hover:bg-[#5652B0]/5 transition-colors group"
                            onClick={() => handleReminderClick(job)}
                            title="Set Reminder">
                            <Bell
                              size={16}
                              className="text-gray-400 group-hover:text-[#5652B0] transition-colors"
                            />
                          </button>
                          <button
                            className="p-2 rounded-lg hover:bg-[#5652B0]/5 transition-colors"
                            onClick={() =>
                              navigate(`/production/jobslips/view?id=${job.id}`)
                            }
                            title="View Job Slip">
                            <Eye size={16} className="text-[#5652B0]" />
                          </button>
                        </div>
                      </div>

                      {/* Second Line: Date, Challan No, and Processing Days */}
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1.5">
                          <Calendar size={14} className="text-gray-400" />
                          <span>
                            <span className="text-gray-500">Created on:</span>{" "}
                            <span className="font-medium">{job.date}</span>
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Hash size={14} className="text-gray-400" />
                          <span>
                            <span className="text-gray-500">Challan No:</span>{" "}
                            <span className="font-medium">
                              {job.manualChallanNo}
                            </span>
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Timer size={14} className="text-gray-400" />
                          <span>
                            <span className="text-gray-500">Processing:</span>{" "}
                            <span className="font-medium">
                              {job.processingDays} days
                            </span>
                          </span>
                        </div>
                      </div>

                      {/* Expected Date - if pending */}
                      {activeTab === "pending" && (
                        <div className="flex items-center gap-2 text-sm mt-1">
                          <Clock size={14} className="text-gray-400" />
                          <span className="text-gray-600">
                            <span className="text-gray-500">
                              Expected Date:
                            </span>{" "}
                            <span className="font-medium text-gray-900">
                              {job.expectedDate}
                            </span>
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Sub Jobs */}
                {job.subJobs && job.subJobs.length > 0 && (
                  <div className="ml-6 mt-2 pl-6 border-l-2 border-dashed border-[#5652B0]/30">
                    {job.subJobs.map(subJob => (
                      <div
                        key={subJob.id}
                        className="relative border border-gray-200 rounded-lg overflow-hidden bg-white hover:border-[#5652B0]/20 hover:shadow-sm transition-all duration-200 mb-2">
                        {/* Connector Line */}
                        <div className="absolute -left-6 top-1/2 w-6 h-px border-t-2 border-dashed border-[#5652B0]/30" />

                        <div className="p-4">
                          <div className="flex flex-col gap-3">
                            {/* First Line: Job ID + Job Type and Karigar */}
                            <div className="flex items-center gap-3">
                              <div className="flex items-center gap-2 bg-[#5652B0]/5 px-3 py-1.5 rounded-md">
                                <span className="text-sm font-medium text-[#5652B0]">
                                  {subJob.id}
                                </span>
                                <span className="text-gray-400">•</span>
                                <span className="text-sm font-medium text-gray-600">
                                  Connected Job
                                </span>
                              </div>
                              <span className="text-sm font-medium text-gray-600 bg-gray-100 px-3 py-1.5 rounded-md flex items-center gap-1.5">
                                <Scissors size={12} />
                                {subJob.jobType}
                              </span>
                              <div className="flex items-center gap-2">
                                <User size={16} className="text-[#5652B0]" />
                                <span className="text-sm font-medium text-[#5652B0]">
                                  {subJob.karigar}
                                </span>
                              </div>

                              {/* Edit and Reminder Buttons */}
                              <div className="ml-auto flex items-center gap-2">
                                <button
                                  className="p-2 rounded-lg hover:bg-[#5652B0]/5 transition-colors group"
                                  onClick={() => handleReminderClick(subJob)}
                                  title="Set Reminder">
                                  <Bell
                                    size={16}
                                    className="text-gray-400 group-hover:text-[#5652B0] transition-colors"
                                  />
                                </button>
                                <button
                                  className="p-2 rounded-lg hover:bg-[#5652B0]/5 transition-colors"
                                  onClick={() =>
                                    navigate(
                                      `/production/jobslips/view?id=${subJob.id}`,
                                    )
                                  }
                                  title="View Job Slip">
                                  <Eye size={16} className="text-[#5652B0]" />
                                </button>
                              </div>
                            </div>

                            {/* Second Line: Creation Date and Processing Days */}
                            <div className="flex items-center gap-4 text-sm text-gray-600">
                              <div className="flex items-center gap-1.5">
                                <Calendar size={14} className="text-gray-400" />
                                <span>
                                  <span className="text-gray-500">
                                    Created on:
                                  </span>{" "}
                                  <span className="font-medium">
                                    {subJob.date}
                                  </span>
                                </span>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <Timer size={14} className="text-gray-400" />
                                <span>
                                  <span className="text-gray-500">
                                    Processing:
                                  </span>{" "}
                                  <span className="font-medium">
                                    {subJob.processingDays} days
                                  </span>
                                </span>
                              </div>
                            </div>

                            {/* Expected Date - if pending */}
                            {activeTab === "pending" && (
                              <div className="flex items-center gap-2 text-sm">
                                <Clock size={14} className="text-gray-400" />
                                <span className="text-gray-600">
                                  <span className="text-gray-500">
                                    Expected Date:
                                  </span>{" "}
                                  <span className="font-medium text-gray-900">
                                    {subJob.expectedDate}
                                  </span>
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

          {/* Empty State */}
          {jobslips.filter(job => job.status === activeTab).length === 0 && (
            <div className="flex flex-col items-center justify-center py-8 px-4 border border-gray-200 rounded-lg bg-gray-50">
              <div className="text-center">
                <p className="mt-2 text-sm text-gray-600">
                  No {activeTab} job slips found
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobslipsComponent;
